const _excluded = ["options", "className", "labelClassName", "id", "style", "inline", "label", "inlineLabel", "postLabelContent", "required"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var s = Object.getOwnPropertySymbols(e); for (r = 0; r < s.length; r++) o = s[r], t.includes(o) || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.includes(n)) continue; t[n] = r[n]; } return t; }
import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, isEqual, isFunction, map } from 'lodash';
import cn from "../../../utils/cn";
import { fieldProps, getPreferredProp } from "../utils";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function RadioField(_ref) {
  let {
      options = [],
      className = "",
      labelClassName = "",
      id = "",
      style = {
        container: "",
        label: "",
        input: "",
        error: ""
      },
      inline = false,
      label = "",
      inlineLabel = false,
      postLabelContent = null,
      required = false
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  const onChange = (value, disabled) => {
    if (!disabled) {
      const callback = getPreferredProp(props, "onChange");
      if (isFunction(callback)) {
        callback(value);
      }
      if (isFunction(props.onChange) && !isEqual(props.onChange, getPreferredProp(props, "onChange"))) {
        props.onChange(value);
      }
    }
  };
  const hasError = getPreferredProp(props, "invalid") && getPreferredProp(props, "touched");
  const error = getPreferredProp(props, "error") || (required ? "Required" : "");
  const containerClass = useMemo(() => cn({
    'border-none my-2 mx-0 p-0 relative mb-2 w-full': true,
    'inline-block mr-2': inline,
    'text-red': hasError
  }, className, style.container), [className, hasError, inline]);
  const labelClass = useMemo(() => cn('text-dark-grey text-xs mb-0 normal-case tracking-normal font-heavy-font leading-4', {
    'text-red': hasError,
    'inline-block pr-6 pl-0': inlineLabel,
    'app-components-form-field__label--required': required
  }, labelClassName, style.label), [inlineLabel, required, hasError, labelClassName]);
  const optionLabelClass = disabled => cn('flex gap-2 items-center tracking-normal leading-loose normal-case text-text-blue font-regular-font text-sm', {
    'cursor-pointer': !disabled,
    'opacity-50': disabled
  });
  const inputClass = (checked, disabled) => cn('appearance-none w-4 h-4 border-2 border-solid rounded-full shrink-0', {
    'border-medium-grey hover:bg-medium-fill-grey cursor-pointer': !checked && !hasError && !disabled,
    'border-action-blue': checked && !hasError,
    'group-hover:border-deep-blue': checked && !hasError && !disabled,
    'border-red': hasError,
    'opacity-50 cursor-not-allowed': disabled,
    'focus:outline-solid-offset': !disabled
  }, style.input);
  const selectedClass = (checked, disabled) => cn('absolute w-2 h-2 rounded-full bg-action-blue ml-1', {
    'group-hover:bg-deep-blue': checked && !disabled,
    'opacity-50': disabled
  });
  return /*#__PURE__*/_jsxs("fieldset", {
    className: containerClass,
    "aria-labelledby": "uu-label-".concat(id),
    id: "".concat(id, "-uu-radio-field__container"),
    "data-testid": "".concat(id, "-uu-radio-field__container"),
    children: [!isEmpty(label) && /*#__PURE__*/_jsx("legend", {
      id: "uu-label-".concat(id),
      "data-testid": "uu-label-".concat(id),
      className: labelClass,
      children: label
    }), postLabelContent, map(options, (opt, index) => {
      const checked = getPreferredProp(props, 'value') === opt.value;
      return /*#__PURE__*/_jsx("ul", {
        className: cn({
          'inline-block mr-3': inline
        }),
        children: /*#__PURE__*/_jsx("li", {
          className: "w-full mb-3",
          children: /*#__PURE__*/_jsxs("div", {
            className: "flex items-center gap-2 group",
            children: [/*#__PURE__*/_jsx("input", {
              className: inputClass(checked, opt.disabled),
              id: "".concat(id, "-uu-radio-field-").concat(index),
              "data-testid": "".concat(id, "-uu-radio-field-").concat(index),
              type: "radio",
              name: getPreferredProp(props, 'name'),
              checked: checked,
              onChange: e => onChange(e, opt.disabled),
              onBlur: getPreferredProp(props, 'onBlur'),
              onFocus: getPreferredProp(props, 'onFocus'),
              disabled: opt.disabled,
              value: opt.value
            }), checked && /*#__PURE__*/_jsx("div", {
              className: selectedClass(checked, opt.disabled)
            }), /*#__PURE__*/_jsx("label", {
              className: optionLabelClass(opt.disabled),
              htmlFor: "".concat(id, "-uu-radio-field-").concat(index),
              id: "".concat(id, "-uu-radio-field-").concat(index, "__label"),
              "data-testid": "".concat(id, "-uu-radio-field-").concat(index, "__label"),
              children: opt.label
            })]
          })
        })
      }, index);
    }), error && hasError && /*#__PURE__*/_jsx("div", {
      className: cn('text-red text-xs', style.error),
      id: "uu-".concat(id, "-radio-field__error"),
      "data-testid": "".concat(id, "-uu-radio-field__error"),
      children: error
    })]
  });
}
RadioField.propTypes = _objectSpread({
  /** id for reference */
  id: PropTypes.string.isRequired,
  /** className for reference */
  className: PropTypes.string,
  /** array of options consist of value and label */
  options: PropTypes.array,
  /** Text of the label */
  label: PropTypes.string,
  /** If true, Radio Buttons will be displayed inline */
  inline: PropTypes.bool,
  /** If true, Radio Label will be displayed inline */
  inlineLabel: PropTypes.bool,
  /** Content block that is placed between the label and the field */
  postLabelContent: PropTypes.node,
  /** Is the field required */
  required: PropTypes.bool,
  /** Override the inline-styles of the elements || **Not Recommended** */
  style: PropTypes.shape({
    container: PropTypes.string,
    label: PropTypes.string,
    text: PropTypes.string,
    error: PropTypes.string
  })
}, fieldProps);
export default RadioField;
//# sourceMappingURL=RadioField.js.map